'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, CheckCircle, Mail } from 'lucide-react'
import { buildApiUrl } from '@/lib/api/config'
import { Logo } from '@/components/Logo'

export default function GetUpdates() {
  const t = useTranslations()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess(false)

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError(t('auth.errors.invalidEmail'))
      setLoading(false)
      return
    }

    try {
      // Call backend API endpoint
      const response = await fetch(buildApiUrl('/api/waitlist'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': navigator.language || 'en',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        // Handle specific error codes
        if (data.code === 'EMAIL_EXISTS') {
          setError(t('comingSoon.errors.emailExists'))
        } else {
          setError(data.error || t('comingSoon.errors.failed'))
        }
      } else {
        setSuccess(true)
        setEmail('')
      }
    } catch (err: any) {
      console.error('Waitlist error:', err)
      setError(err.message || t('comingSoon.errors.failed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card className="mx-auto w-full max-w-md text-left shadow-float">
      <CardHeader className="text-center">
        <div className="mb-4 flex justify-center">
          <Logo size="lg" markOnly />
        </div>
        <CardTitle className="text-2xl">{t('comingSoon.title')}</CardTitle>
        <CardDescription className="text-base">{t('comingSoon.description')}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 rounded-lg bg-success/10 p-3 text-sm text-success">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>{t('comingSoon.success')}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="waitlist-email">{t('comingSoon.emailLabel')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="waitlist-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading || success}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex items-start gap-2.5 rounded-lg bg-muted p-3">
            <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-speaker-1" aria-hidden="true" />
            <p className="text-sm text-secondary-foreground">
              {t('comingSoon.benefits')}
            </p>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4">
          <Button
            type="submit"
            className="w-full"
            disabled={loading || success}
          >
            {loading ? t('common.loading') : t('comingSoon.submit')}
          </Button>

          <p className="text-center text-xs text-muted-foreground">
            {t('comingSoon.privacy')}
          </p>
        </CardFooter>
      </form>
    </Card>
  )
}
